import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import { fetchBrandRadarHits } from './service';
import type { TrendBrandRadarHit } from './types';
import {
  getRunPresentationColor,
  getRunPresentationLabel,
  isLatestRunPresentation,
  sortBySeverityThenRunPresentation,
} from './runPresentationUtils';
import { PAGE_HEADING_SX, TREND_COLORS } from './trendTheme';
import TrendEmptyState from './components/TrendEmptyState';
import TrendScore from './components/TrendScore';

function classificationLabel(value: TrendBrandRadarHit['classification']): string {
  if (value === 'risk') return 'Risk';
  if (value === 'opportunity') return 'Möjlighet';
  return 'Ingen åtgärd';
}

function sentimentLabel(value: TrendBrandRadarHit['sentiment']): string {
  if (value === 'positive') return 'Positiv';
  if (value === 'negative') return 'Negativ';
  if (value === 'mixed') return 'Blandad';
  return 'Neutral';
}

function statusLabel(value: TrendBrandRadarHit['status']): string {
  if (value === 'preview') return 'Testträff';
  if (value === 'draft') return 'För granskning';
  if (value === 'reviewed') return 'Granskad';
  if (value === 'archived') return 'Arkiverad';
  return value;
}

export default function TrendsBrandRadarPage() {
  const [hits, setHits] = useState<TrendBrandRadarHit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    void fetchBrandRadarHits({ limit: 50 })
      .then((result) => {
        if (!cancelled) setHits(result);
      })
      .catch(() => {
        if (!cancelled) setError('Kunde inte ladda Brand Radar.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box sx={{ bgcolor: TREND_COLORS.pageBg, minHeight: '100vh', mx: -3, mt: -3, px: { xs: 2, md: 4 }, py: { xs: 3, md: 5 } }}>
      <Stack spacing={3} sx={{ maxWidth: 1100, mx: 'auto' }}>
        <Stack spacing={1}>
          <Typography sx={PAGE_HEADING_SX}>BRAND RADAR</Typography>
          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, fontStyle: 'italic' }}>
            Omnämnanden, risker och möjligheter kopplade till Fontana Food.
          </Typography>
        </Stack>

        {loading && <CircularProgress sx={{ color: TREND_COLORS.navy }} />}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && hits.length === 0 && (
          <TrendEmptyState
            title="Inga aktuella Brand Radar-träffar"
            description="Träffar visas här när en körning hittat omnämnanden kopplade till Fontana Food."
          />
        )}

        {!loading && !error && sortBySeverityThenRunPresentation(hits).map((hit) => (
          <Paper key={hit._id} sx={{ p: 2, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ flexGrow: 1, fontWeight: 700, color: TREND_COLORS.navy }}>{hit.title || 'Brand Radar-träff'}</Typography>
                {isLatestRunPresentation(hit.runPresentation) && (
                  <Chip size="small" color={getRunPresentationColor(hit.runPresentation)} label={getRunPresentationLabel(hit.runPresentation)} />
                )}
                <Chip size="small" color={hit.classification === 'risk' ? 'error' : hit.classification === 'opportunity' ? 'success' : 'default'} label={classificationLabel(hit.classification)} />
                <Chip size="small" label={sentimentLabel(hit.sentiment)} variant="outlined" />
                <TrendScore label="Prioritet" value={hit.severityScore} />
              </Stack>
              <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary }}>{hit.snippet}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Sökterm: ${hit.matchedTerm || hit.query}`} />
                <Chip size="small" label={statusLabel(hit.status)} variant="outlined" />
                {hit.publishedAt && <Chip size="small" label={hit.publishedAt.slice(0, 10)} variant="outlined" />}
              </Stack>
              {hit.url && (
                <Box>
                  <Button size="small" href={hit.url} target="_blank" rel="noreferrer">
                    Öppna originalkälla
                  </Button>
                </Box>
              )}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
